/**
 * Imposition — laying one image up many times on a press sheet (a "gang run").
 *
 * This is the print-lab counterpart to the design studio: instead of composing
 * one artwork, the customer picks a photo or a piece of art and we tile it
 * across a sheet at a chosen cell size — passport photos, stickers, wallet
 * prints — then cut it down. Everything here is pure and in millimetres, the
 * same unit the studio and the pdf-lib renderer already speak, so one geometry
 * drives the on-screen preview and the print output alike.
 */
export const PAPER_TYPES = [
    { id: '90gsm', label: '90 gsm' },
    { id: '130gsm', label: '130 gsm' },
    { id: '170gsm', label: '170 gsm' },
    { id: '210gsm', label: '210 gsm' },
    { id: '300gsm', label: '300 gsm' },
    { id: 'sticker', label: 'Sticker paper' },
    { id: 'vinyl', label: 'Vinyl' },
];
export const LAMINATIONS = [
    { id: 'none', label: 'None' },
    { id: 'matte', label: 'Matte' },
    { id: 'gloss', label: 'Gloss' },
    { id: 'signage', label: 'Signage' },
    { id: 'velvet', label: 'Velvet' },
];
/** Common digital-press sheet sizes, in millimetres. */
export const SHEET_PRESETS = [
    { id: '13x19', label: '13" × 19"', widthMm: 330.2, heightMm: 482.6 },
    { id: '12x18', label: '12" × 18"', widthMm: 304.8, heightMm: 457.2 },
    { id: 'sra3', label: 'SRA3', widthMm: 320, heightMm: 450 },
    { id: 'a3', label: 'A3', widthMm: 297, heightMm: 420 },
    { id: 'a4', label: 'A4', widthMm: 210, heightMm: 297 },
];
/** Standard photo / print cell sizes (width × height, mm). */
export const PHOTO_PRESETS = [
    { id: 'passport-in-us', label: 'Passport (IN/US) 51×51', widthMm: 51, heightMm: 51 },
    { id: 'passport-eu', label: 'Passport (EU) 35×45', widthMm: 35, heightMm: 45 },
    { id: 'visa', label: 'Visa 33×48', widthMm: 33, heightMm: 48 },
    { id: 'id', label: 'ID 25×35', widthMm: 25, heightMm: 35 },
    { id: 'wallet', label: 'Wallet 50×70', widthMm: 50, heightMm: 70 },
];
export const CAPTION_HEIGHT_MM = 4;
const round1 = (n) => Math.round(n * 10) / 10;
/** The sheet's dimensions after applying its orientation — width is the shorter
 * edge in portrait, the longer edge in landscape. */
export function orientedSheet(sheet) {
    const a = Math.min(sheet.widthMm, sheet.heightMm);
    const b = Math.max(sheet.widthMm, sheet.heightMm);
    return sheet.orientation === 'landscape' ? { widthMm: b, heightMm: a } : { widthMm: a, heightMm: b };
}
/**
 * How many cells fit and exactly where, given a sheet and a cell spec.
 *
 * A row holds `cols` cells with `gapX` between them, inside the width left after
 * both `marginX`s — so `cols = floor((avail + gap) / (cell + gap))`, the +gap
 * accounting for the last cell needing no trailing gap. The grid is anchored at
 * the margin (top-left); any slack falls on the far side, which keeps the near
 * margin exactly as specified rather than drifting with the count.
 */
export function computeImposition(sheetWidthMm, sheetHeightMm, cell) {
    const availW = sheetWidthMm - 2 * cell.marginXMm;
    const availH = sheetHeightMm - 2 * cell.marginYMm;
    const fit = (avail, size, gap) => size <= 0 || avail < size ? 0 : Math.floor((avail + gap) / (size + gap));
    const cols = fit(availW, cell.widthMm, cell.gapXMm);
    const rows = fit(availH, cell.heightMm, cell.gapYMm);
    const usedWidthMm = cols > 0 ? cols * cell.widthMm + (cols - 1) * cell.gapXMm : 0;
    const usedHeightMm = rows > 0 ? rows * cell.heightMm + (rows - 1) * cell.gapYMm : 0;
    const cells = [];
    for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
            cells.push({
                xMm: round1(cell.marginXMm + c * (cell.widthMm + cell.gapXMm)),
                yMm: round1(cell.marginYMm + r * (cell.heightMm + cell.gapYMm)),
                widthMm: cell.widthMm,
                heightMm: cell.heightMm,
            });
        }
    }
    return { cols, rows, perSheet: cols * rows, cells, usedWidthMm: round1(usedWidthMm), usedHeightMm: round1(usedHeightMm) };
}
/** Sheets needed to yield `photosNeeded` copies, and the prints that produces
 * (a sheet is indivisible, so the last one is usually part-waste). */
export function quantityPlan(perSheet, photosNeeded) {
    if (perSheet <= 0 || photosNeeded <= 0)
        return { sheets: 0, prints: 0 };
    const sheets = Math.ceil(photosNeeded / perSheet);
    return { sheets, prints: sheets * perSheet };
}
/** A sensible default job for a fresh print-lab session: 13×19 portrait, EU
 * passport cells, 3mm gaps, 5mm margins, bleed and cut marks on. */
export function emptyImpositionJob() {
    return {
        sheet: {
            widthMm: 330.2,
            heightMm: 482.6,
            orientation: 'portrait',
            paper: '170gsm',
            lamination: 'none',
            sides: 'single',
        },
        layoutMode: 'arrangement',
        cell: { widthMm: 35, heightMm: 45, gapXMm: 3, gapYMm: 3, marginXMm: 5, marginYMm: 5 },
        adjust: { fit: 'fill', scale: 1, rotationDeg: 0, flipH: false, flipV: false, nudgeXMm: 0, nudgeYMm: 0 },
        guides: { bleedMm: 3, safeInsetMm: 3, showBleed: true, showSafe: false, showCutMarks: true },
    };
}
//# sourceMappingURL=imposition.js.map